import {
  type AnyAction,
  configureStore,
  type Middleware,
  type ReducersMapObject,
} from "@reduxjs/toolkit";
import { setupListeners } from "@reduxjs/toolkit/query";
import { createLogger } from "redux-logger";

import { isLocal } from "@/common/utilities/env";

/**
 * What every app's store is built from (GEN5-5269 ARCHITECTURE A5, item 5).
 * The factory creates nothing at import: each app's entry calls it once, and
 * shared code only ever sees the store through `state/appHooks`.
 */
export type AppStoreOptions<State, Extra> = {
  /** Typed by the state they build, so the store's state is inferred at the call, as `configureStore`'s is. */
  reducers: ReducersMapObject<State, AnyAction>;
  /** After the default middleware (thunk, serializable and immutable checks in development). */
  middleware?: readonly Middleware[];
  /** The thunk's `extraArgument`: what the app injects for thunks and base queries to read. */
  extra: Extra;
  /** Action type prefixes the local logger leaves out (RTK Query's own traffic). */
  loggerExcludes?: readonly string[];
  /** RTK Query's refetch-on-focus/reconnect listeners. */
  refetchListeners?: boolean;
};

// The one cast left here, as before in each app's store: @types/redux-logger is typed against
// redux 5's Middleware (UnknownAction), RTK 1.9 against redux 4's (AnyAction).
const createActionLogger = (excludes: readonly string[]): Middleware =>
  createLogger({
    collapsed: true,
    predicate: (getState, action) =>
      !excludes.some((excluded) => action.type.startsWith(excluded)),
    duration: true,
  }) as Middleware;

export function createAppStore<State, Extra>({
  reducers,
  middleware = [],
  extra,
  loggerExcludes = [],
  refetchListeners = true,
}: AppStoreOptions<State, Extra>) {
  const store = configureStore({
    reducer: reducers,
    middleware: (getDefaultMiddleware) => {
      const defaults = getDefaultMiddleware({
        thunk: { extraArgument: extra },
      });
      return isLocal()
        ? defaults.concat(createActionLogger(loggerExcludes), ...middleware)
        : defaults.concat(...middleware);
    },
    devTools: import.meta.env.MODE !== "production",
  });
  if (refetchListeners) {
    // Required for refetchOnFocus/refetchOnReconnect.
    setupListeners(store.dispatch);
  }
  return store;
}
